import React, { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import api, { fileViewUrl, formatErr } from "../lib/api";
import { toast } from "sonner";

export default function PhotoUpload({ value = [], onChange, multiple = true, maxCount = null, testId = "photo-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const reachedLimit = maxCount && value.length >= maxCount;

  const handleFiles = async (files) => {
    if (!files?.length) return;
    let toUpload = Array.from(files);
    if (maxCount) {
      const remaining = Math.max(0, maxCount - value.length);
      if (remaining === 0) {
        toast.error(`Maximum ${maxCount} photo${maxCount > 1 ? "s" : ""} allowed`);
        return;
      }
      if (toUpload.length > remaining) {
        toast.error(`Only ${remaining} more photo${remaining > 1 ? "s" : ""} can be added`);
        toUpload = toUpload.slice(0, remaining);
      }
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.path);
      }
      const next = multiple ? [...value, ...uploaded] : uploaded.slice(0, 1);
      onChange(next);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (p) => onChange(value.filter((x) => x !== p));

  return (
    <div className="space-y-3" data-testid={testId}>
      {!reachedLimit && (
        <div
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer border-2 border-dashed border-brand-border bg-brand-surfaceAlt rounded-lg p-6 text-center hover:bg-[#EBE8E0] transition-colors"
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <div className="text-sm text-brand-muted">Uploading…</div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-white border border-brand-border grid place-items-center">
                <Camera className="h-5 w-5 text-brand-primary" />
              </div>
              <div className="text-sm font-medium">Tap to take photo or upload</div>
              <div className="text-xs text-brand-muted">
                JPG, PNG, WEBP up to 10MB
                {maxCount ? ` · ${value.length}/${maxCount} photos` : ""}
              </div>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple={multiple}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid={`${testId}-input`}
          />
        </div>
      )}

      {reachedLimit && (
        <div className="rounded-md border border-brand-border bg-brand-surfaceAlt px-4 py-3 text-xs text-brand-muted">
          Limit reached ({maxCount}). Remove a photo to add another.
        </div>
      )}

      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {value.map((p) => (
            <div key={p} className="relative aspect-square rounded-md overflow-hidden border border-brand-border bg-white">
              <img src={fileViewUrl(p)} alt="upload" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(p)}
                className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black"
                data-testid={`${testId}-remove`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
